import { sectionsFor } from '@kronos/domain'
import type { Hub, Role, Section } from '@kronos/domain'
import { BuildsView } from '../BuildsView'
import { PlanView } from '../PlanView'
import { TeamView } from '../TeamView'
import { OfficeHoursView } from '../hours/OfficeHoursView'
import { TeamScheduleView } from '../schedule/TeamScheduleView'
import { PageHeader } from '../primitives'
import { Link } from './Link'

/**
 * The two places with more than one job in them. Each shows a title, the tabs
 * this person holds (`sectionsFor`), and the screen for the one chosen — which
 * is in the URL, so a reload or a shared link lands on the same tab.
 */

const LABEL: Readonly<Record<Section, string>> = {
  orders: 'Orders',
  'to-buy': 'To buy',
  schedule: 'Schedule',
  hours: 'Hours',
  team: 'Team',
}

function Tabs({ hub, sections, current }: { hub: 'build' | 'people'; sections: readonly Section[]; current: Section }): JSX.Element | null {
  if (sections.length < 2) return null
  return (
    <nav className="section-tabs" aria-label="Sections">
      {sections.map((s) => (
        <Link key={s} to={{ name: hub, section: s }} className="section-tabs__tab" current={s === current}>
          {LABEL[s]}
        </Link>
      ))}
    </nav>
  )
}

/** The section asked for, if this person has it; otherwise their first. */
function choose(roles: readonly Role[], hub: Hub, asked: Section | null): { sections: readonly Section[]; current: Section | null } {
  const sections = sectionsFor(roles, hub)
  return { sections, current: asked && sections.includes(asked) ? asked : (sections[0] ?? null) }
}

export function BuildPlace({ roles, section }: { roles: readonly Role[]; section: Section | null }): JSX.Element {
  const { sections, current } = choose(roles, 'build', section)
  return (
    <div className="page">
      <PageHeader title="Build" subtitle="What has been ordered, what can start, and what to buy." />
      {current === null ? (
        <p className="muted">Builds are planned by engineers.</p>
      ) : (
        <>
          <Tabs hub="build" sections={sections} current={current} />
          {current === 'orders' ? <BuildsView roles={roles} /> : null}
          {current === 'to-buy' ? <PlanView /> : null}
        </>
      )}
    </div>
  )
}

export function PeoplePlace({ roles, section }: { roles: readonly Role[]; section: Section | null }): JSX.Element {
  const { sections, current } = choose(roles, 'people', section)
  return (
    <div className="page">
      <PageHeader title="People" subtitle="Who is coming in, the hours they worked, and who may do what." />
      {current === null ? (
        <p className="muted">People is for engineers and admins.</p>
      ) : (
        <>
          <Tabs hub="people" sections={sections} current={current} />
          {current === 'schedule' ? <TeamScheduleView /> : null}
          {current === 'hours' ? <OfficeHoursView /> : null}
          {current === 'team' ? <TeamView roles={roles} /> : null}
        </>
      )}
    </div>
  )
}
